import {
  fetchResubscriptionModal,
  type ResubscriptionModalData,
  type SubscriptionSettingsAnalytics,
} from '@library/api-customer-engagement-experience'

import {
  isRenderableResubscriptionModalData,
  isValidAnalytics,
} from './isRenderableResubscriptionModalData'

export const RESUBSCRIPTION_MODAL_TIMEOUT_MS = 3000

export type StagedResubscriptionModal = {
  data: ResubscriptionModalData
  pageLoad?: SubscriptionSettingsAnalytics
}

let staged: StagedResubscriptionModal | null = null

const TIMED_OUT = Symbol('timed-out')

export const peekStagedResubscriptionModal = (): StagedResubscriptionModal | null => staged

export const clearStagedResubscriptionModal = (): void => {
  staged = null
}

/**
 * Fetches the confirmation modal content and holds it for the menu to show once.
 * Always resolves: a 204, an error, a malformed body or a timeout stages nothing.
 */
export const stageResubscriptionModal = async (): Promise<void> => {
  staged = null

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), RESUBSCRIPTION_MODAL_TIMEOUT_MS)
  })

  try {
    const result = await Promise.race([fetchResubscriptionModal(), timeout])

    if (
      result !== TIMED_OUT &&
      result.status === 200 &&
      isRenderableResubscriptionModalData(result.data)
    ) {
      staged = {
        data: result.data,
        pageLoad: isValidAnalytics(result.pageLoad) ? result.pageLoad : undefined,
      }
    }
  } catch {
    staged = null
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer)
    }
  }
}
